import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { TourPriceOption } from '@/types/tourBooking';
import { formatPriceWithNoUnit } from '@/utils/formatPrice';
import { BookingFormSection } from '@/components/BookingFormCustom';
import { toast } from 'react-toastify';

interface TourPriceOptionsSectionProps {
  tourPriceOptions: TourPriceOption[];
  selectedTourPriceOptionId: string;
  onSelectTourPriceOptionId: (id: string) => void;
  onOpenMobileDrawer?: () => void;
}

const TourPriceOptionsSection: React.FC<TourPriceOptionsSectionProps> = ({
  tourPriceOptions,
  selectedTourPriceOptionId,
  onSelectTourPriceOptionId,
  onOpenMobileDrawer,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['validate', 'common', 'error']);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSelectOption = (id: string) => {
    onSelectTourPriceOptionId(id);
    if (isMobile && onOpenMobileDrawer) {
      onOpenMobileDrawer();
    }
  };

  return (
    <BookingFormSection sx={{ paddingTop: '0' }}>
      <Typography variant='h5' fontWeight={400} fontSize={{ xs: 12, md: 14 }} mb={1}>
        {t('common:choose_tour_route')}<Typography component="span" color="error.main">*</Typography>
      </Typography>
      {tourPriceOptions && tourPriceOptions.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            gap: 1.5,
            width: '100%',
            maxHeight: { xs: tourPriceOptions.length > 3 ? 280 : 'auto', md: 'auto' },
            overflowY: { xs: tourPriceOptions.length > 3 ? 'auto' : 'visible', md: 'visible' },
            overflowX: { xs: 'hidden', md: tourPriceOptions.length > 6 ? 'auto' : 'visible' },
            ...(tourPriceOptions.length > 6 && {
              flexWrap: { md: 'nowrap' },
            }),
          }}
        >
          {tourPriceOptions.map((option, index) => {
            const isSelected = selectedTourPriceOptionId === option.id;
            const title = option.contents?.[0]?.title || `${t('common:tourDuration')} ${option.duration} ${t('common:minutes')}`;
            const price = option.tourPricing?.defaultPrice || option.defaultPrice || 0;
            return (
              <Box
                key={`${option.id}-${index}`}
                onClick={() => {
                  const tourPricing = option.tourPricing;
                  if (!tourPricing) {
                    toast.error(t('tour.tourPricingNotFound'));
                  }
                  handleSelectOption(option.id);
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  padding: '6px',
                  borderRadius: "6px",
                  border: `1px solid ${isSelected ? theme.palette.yellow.deepPeach : theme.palette.black.silver}`,
                  backgroundColor: isSelected ? theme.palette.yellow.papayaWhip : theme.palette.white.main,
                  cursor: 'pointer',
                  minWidth: { xs: '100%', md: 200 },
                  width: { xs: '100%', md: 'calc(33.33% - 12px)' },
                  flexShrink: 0,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: `${isSelected ? theme.palette.yellow.deepPeach : theme.palette.black.silver}CC`,
                    backgroundColor: `${isSelected ? theme.palette.yellow.papayaWhip : theme.palette.white.main}CC`,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{
                    color: isSelected ? theme.palette.other.burntOrange : theme.palette.other.gray700, mb: 0.5
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={400}
                  sx={{ color: isSelected ? theme.palette.other.burntOrange : theme.palette.black.jetBlack }}
                >
                  {formatPriceWithNoUnit(price)} vnđ + {t('common:visit_fee')}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 80 }}>
          <Typography variant='body1' fontWeight={400} textAlign="center" color="text.secondary">
            {t('common:noData')}
          </Typography>
        </Box>
      )}
    </BookingFormSection>
  );
};

export default TourPriceOptionsSection;
